export const RELEASE_2B_REFERENCE_SHA256 = "62701c43407edd4c65fc38c72663fc4456c2b742e9963aaad7569e2c43ad884c";

export const PHOTOGRAPHY_PACKAGES = Object.freeze([
  { id: "VUE-PRIME", name: "VUE Prime", basePrice: 220 },
  { id: "VUE-LUXE", name: "VUE Luxe", basePrice: 270 },
  { id: "ZILLOW-SHOWCASE", name: "Zillow Showcase", basePrice: 350 }
]);

export const STANDARD_MARKETING_RESERVES = Object.freeze({
  justListed: 61,
  justSold: 61,
  closingGift: 25
});

export const ACTIVE_ROOTS = Object.freeze([
  { id: "ROOTS-L-2407", fubDealId: "FUB-D-88012", address: "81 Maple Grove Lane, Murrysville, PA 15668", side: "Seller", status: "Listing" },
  { id: "ROOTS-T-3901", fubDealId: "FUB-D-77777", address: "4421 Willow Park Drive, Pittsburgh, PA 15235", side: "Buyer", status: "Pending" },
  { id: "ROOTS-T-3902", fubDealId: "", address: "77 Harbor View Drive, Pittsburgh, PA 15238", side: "Seller", status: "Listing" }
]);

// Release 2B.1 keeps the existing Listing/Pre-List preview source in scope while
// removing all fictional FUB People from the runtime. Listing selection never
// supplies a client identity; Primary Seller selection is a separate live FUB read.
export const FICTIONAL_ROOTS_LISTINGS = Object.freeze([
  { id: "ROOTS-L-2407", address: "81 Maple Grove Lane, Murrysville, PA 15668", propertyType: "Single Family", listPrice: 585000, mls: "MOCK-24115", fubDealId: "FUB-D-88012", status: "Listing" },
  { id: "ROOTS-L-2411", address: "14 Hemlock Court, Monroeville, PA 15146", propertyType: "Townhome", listPrice: 329000, mls: "MOCK-24129", fubDealId: "FUB-D-88044", status: "Pre-List" }
]);

const MARKETING_PRICE_MULTIPLIER = 1.07;
const BASE_STATUS_LOCKED = new Set(["Awaiting Leadership Approval", "Approved", "Operations Intake", "Pre-List", "Listing", "Pending"]);

export function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createBaseRecord(kind, title) {
  return {
    kind,
    title,
    status: "Draft",
    step: 0,
    client: null,
    client2: null,
    buyer2Applicable: false,
    agent: "",
    agentEmail: "",
    assignedAgentId: "",
    assignedAgentMatchesIntakeUser: null,
    intakeUserEmail: "",
    intakeUserName: "",
    source: "",
    systemDealId: "",
    address: "",
    docs: [],
    notes: "",
    saved: false,
    dirty: false,
    needsChangesComment: "",
    duplicateAcknowledged: false,
    duplicateResolutionNote: "",
    duplicateAckBasis: "",
    agentMismatchReviewed: false,
    agentMismatchReviewBasis: "",
    reviewHistory: [],
    decisions: { base: "Pending" }
  };
}

export function createListingRecord() {
  return {
    ...createBaseRecord("listing", "New Listing Agreement"),
    propertyType: "",
    beds: "",
    baths: "",
    propertyNotes: "",
    agreementDate: "",
    expirationDate: "",
    listPrice: "",
    commissionRate: "",
    targetLiveDate: "",
    signRequested: false,
    signTiming: "",
    showingInstructions: "",
    accessNotes: "",
    restrictedAccess: "",
    hasExistingLockbox: null,
    existingLockboxSerial: "",
    photoPackageId: "",
    photoAddons: [],
    requiredMarketing: { justListed: false, justSold: false, closingGift: false },
    optionalResourceRequests: {},
    otherMarketingRequest: "",
    marketingAllocationException: "",
    marketingRequiredExceptionApproved: false,
    marketingRequiredExceptionBasis: "",
    marketingRequiredExceptionVersionId: "",
    marketingSubmitted: false,
    marketingSubmittedVersionId: "",
    marketingSubmittedBudgetBasis: "",
    marketingDecisionVersionId: "",
    marketingRevisionRequired: false,
    marketingVersionCounter: 0,
    marketingVersions: [],
    marketingExecutionLineCounter: 0,
    marketingExecutionLines: [],
    allowMarketingEdit: false,
    openHouseRequested: false,
    openHouseSubmitted: false,
    openHouseSubmittedVersionId: "",
    openHouseDecisionVersionId: "",
    openHouseVersionCounter: 0,
    openHouseVersions: [],
    openHouseRequestId: "OH-001",
    openHouseDate: "",
    openHouseStart: "",
    openHouseEnd: "",
    openHouseHost: "",
    openHouseInstructions: "",
    openHouseOpsConfirmed: false,
    allowOpenHouseEdit: false,
    decisions: { base: "Pending", budget: "Pending", openHouse: "Pending" },
    autoOps: {
      photo: "Waiting for Pre-List",
      justListed: "Waiting for Listing/live",
      justSold: "Waiting for Closed/Sold",
      optional: "Waiting for Marketing Budget Approval"
    }
  };
}

export function createPendingRecord(kind) {
  return {
    ...createBaseRecord(kind, kind === "buyer" ? "Buyer Pending Transaction" : "Seller Pending Transaction"),
    linkedListing: null,
    rootsTransactionId: "",
    mls: "",
    salePrice: "",
    acceptanceDate: "",
    closingDate: "",
    financing: "",
    earnestAmount: "",
    earnestDue: "",
    lender: "",
    titleParty: "",
    cooperatingAgent: "",
    buyerAgent: "",
    specialContractNotes: "",
    tcContractReviewComplete: false,
    tcContractReviewNotes: ""
  };
}

export function clearFubDerived(record, intakeUser = {}) {
  const next = clone(record);
  next.client = null;
  next.client2 = null;
  next.buyer2Applicable = false;
  next.agent = "";
  next.agentEmail = "";
  next.assignedAgentId = "";
  next.assignedAgentMatchesIntakeUser = null;
  next.intakeUserEmail = String(intakeUser?.email || next.intakeUserEmail || "").trim().toLowerCase();
  next.intakeUserName = String(intakeUser?.name || next.intakeUserName || "").trim();
  next.source = "";
  next.agentMismatchReviewed = false;
  next.agentMismatchReviewBasis = "";
  next.dirty = true;
  return next;
}

export function applyFubDerived(record, person, intakeUser = {}) {
  if (!person) return clearFubDerived(record, intakeUser);
  const next = clearFubDerived(record, intakeUser);
  next.client = clone(person);
  next.agent = String(person?.assignedAgent?.name || "").trim();
  next.assignedAgentId = String(person?.assignedAgent?.id || "").trim();
  next.assignedAgentMatchesIntakeUser = typeof person?.assignedAgent?.matchesIntakeUser === "boolean"
    ? person.assignedAgent.matchesIntakeUser
    : null;
  next.source = String(person?.leadSource || "").trim();
  next.dirty = true;
  return next;
}

export function applyLinkedListing(record, listing) {
  const next = clearFubDerived(record);
  next.linkedListing = clone(listing);
  next.rootsTransactionId = listing.id;
  next.address = listing.address;
  next.mls = listing.mls;
  next.systemDealId = listing.fubDealId;
  next.dirty = true;
  return next;
}

export function calcGci(record) {
  if (record?.kind !== "listing") return null;
  const price = Number(record.listPrice);
  const rate = Number(record.commissionRate);
  if (!(price > 0) || !(rate > 0)) return null;
  return price * (rate / 100);
}

export function marketingBudget(record) {
  const gci = calcGci(record);
  if (gci === null) return { ready: false, gci: null, raw: null, cap: null, eligible: false };
  const raw = gci * 0.08;
  return { ready: true, gci, raw, cap: Math.min(raw, 1000), eligible: gci >= 4000 };
}

export function finalMarketingPrice(basePrice) {
  return Math.round((Number(basePrice || 0) * MARKETING_PRICE_MULTIPLIER + Number.EPSILON) * 100) / 100;
}

export function photoPackage(record) {
  return PHOTOGRAPHY_PACKAGES.find(item => item.id === record?.photoPackageId) || null;
}

export function marketingLineItems(record) {
  if (record?.kind !== "listing") return [];
  const lines = [];
  const pkg = photoPackage(record);
  if (pkg) lines.push({ id: "photo-package", label: pkg.name, amount: finalMarketingPrice(pkg.basePrice) });
  if (record.requiredMarketing?.justListed) lines.push({ id: "just-listed", label: "Just Listed Postcard Campaign", amount: finalMarketingPrice(STANDARD_MARKETING_RESERVES.justListed) });
  if (record.requiredMarketing?.justSold) lines.push({ id: "just-sold", label: "Just Sold Postcard Campaign", amount: finalMarketingPrice(STANDARD_MARKETING_RESERVES.justSold) });
  if (record.requiredMarketing?.closingGift) lines.push({ id: "closing-gift", label: "Closing Gift", amount: finalMarketingPrice(STANDARD_MARKETING_RESERVES.closingGift) });
  return lines;
}

export function requiredMarketingState(record) {
  const pkg = photoPackage(record);
  const req = record?.requiredMarketing || {};
  const selected = Boolean(pkg && req.justListed && req.justSold && req.closingGift);
  const requiredCost = pkg
    ? finalMarketingPrice(pkg.basePrice)
      + finalMarketingPrice(STANDARD_MARKETING_RESERVES.justListed)
      + finalMarketingPrice(STANDARD_MARKETING_RESERVES.justSold)
      + finalMarketingPrice(STANDARD_MARKETING_RESERVES.closingGift)
    : null;
  const budget = marketingBudget(record);
  const cannotFit = Boolean(budget.ready && requiredCost !== null && requiredCost > budget.cap + 0.0001);
  const exceptionApproved = Boolean(
    record?.marketingRequiredExceptionApproved
    && record.marketingRequiredExceptionBasis === marketingBudgetBasisSignature(record)
    && record.marketingRequiredExceptionVersionId
    && record.marketingRequiredExceptionVersionId === record.marketingSubmittedVersionId
  );
  return { selected, requiredCost, cannotFit, exceptionApproved };
}

export function marketingAllocation(record) {
  const budget = marketingBudget(record);
  const required = requiredMarketingState(record);
  const allocated = Math.round((marketingLineItems(record).reduce((sum, line) => sum + line.amount, 0) + Number.EPSILON) * 100) / 100;
  const remaining = budget.cap === null ? null : Math.max(0, Math.round((budget.cap - allocated + Number.EPSILON) * 100) / 100);
  const overage = budget.cap === null ? 0 : Math.max(0, Math.round((allocated - budget.cap + Number.EPSILON) * 100) / 100);
  return {
    potential: budget.cap,
    allocated,
    remaining,
    overage,
    overBudget: overage > 0.0001,
    requiredComplete: required.selected,
    requiredCannotFit: required.cannotFit,
    requiredExceptionApproved: required.exceptionApproved,
    reviewRequired: Boolean(!budget.eligible || required.cannotFit || overage > 0.0001)
  };
}

export function marketingBudgetBasisSignature(record) {
  return JSON.stringify({ listPrice: Number(record?.listPrice || 0), commissionRate: Number(record?.commissionRate || 0) });
}

export function duplicateControlSignature(record) {
  const side = record?.kind === "listing" || record?.kind === "seller" ? "Seller" : "Buyer";
  return [String(record?.address || "").trim().toLowerCase(), record?.linkedListing?.id || "", record?.systemDealId || "", side].join("|");
}

export function duplicateResolutionCurrent(record) {
  return Boolean(
    record?.duplicateAcknowledged
    && record.duplicateAckBasis === duplicateControlSignature(record)
    && String(record.duplicateResolutionNote || "").trim()
  );
}

export function duplicateEvidenceEditable(record) {
  return Boolean(record && record.status === "Awaiting Leadership Approval" && record.decisions?.base === "Pending");
}

export function duplicateCheck(record, activeRoots = ACTIVE_ROOTS) {
  const linkedId = record?.kind === "seller" && record.linkedListing ? record.linkedListing.id : null;
  let blocked = false;
  let warning = false;
  let blockedMsg = "";
  let warningMsg = "";
  if (record?.systemDealId) {
    const other = activeRoots.find(item => item.fubDealId && item.fubDealId === record.systemDealId && item.id !== linkedId);
    if (other) {
      blocked = true;
      blockedMsg = `System-managed durable FUB identity already maps to active ROOTS transaction ${other.id}. Duplicate creation is blocked.`;
    }
  }
  if (record?.address) {
    const side = record.kind === "listing" || record.kind === "seller" ? "Seller" : "Buyer";
    const hit = activeRoots.find(item => item.address.toLowerCase() === record.address.toLowerCase() && item.side === side && item.status !== "Closed" && item.id !== linkedId);
    if (hit) {
      warning = true;
      warningMsg = `ROOTS found another open ${side} record at this property. Leadership must acknowledge the warning and record a resolution reason.`;
    }
  }
  return { blocked, warning, blockedMsg, warningMsg, resolved: warning ? duplicateResolutionCurrent(record) : true };
}

export function agentMismatch(record) {
  return Boolean(
    record?.client
    && record.assignedAgentId
    && record.assignedAgentMatchesIntakeUser === false
  );
}

export function submissionGates(record) {
  const baseBlocks = [];
  const marketingBlocks = [];
  const baseLeadership = [];
  const marketingLeadership = [];
  const openHouseLeadership = [];
  const operations = [];
  const advisory = [];

  if (!record?.client) baseBlocks.push("Primary FUB client");
  if (!record?.address) baseBlocks.push("Property address");

  if (record?.kind === "listing") {
    if (!record.agreementDate) baseBlocks.push("Agreement / execution date");
    if (!record.expirationDate) baseBlocks.push("Expiration date");
    if (!(Number(record.listPrice) > 0)) baseBlocks.push("Proposed list price");
    if (!(Number(record.commissionRate) > 0)) baseBlocks.push("Valid Estimated Listing-Side Commission %");
    const budget = marketingBudget(record);
    const allocation = marketingAllocation(record);
    const required = requiredMarketingState(record);
    if (!budget.ready) marketingBlocks.push("Potential Marketing Budget cannot be calculated");
    if (!required.selected) marketingBlocks.push("Required marketing selections: Photography, Just Listed, Just Sold, and Closing Gift");
    if (required.selected && allocation.overBudget) {
      const onlyRequiredOver = required.cannotFit && allocation.allocated <= required.requiredCost + 0.0001;
      if (onlyRequiredOver && !required.exceptionApproved) marketingLeadership.push("Required marketing cannot fit within the Potential Marketing Budget; Leadership exception required");
      if (!onlyRequiredOver) marketingBlocks.push("Selected Marketing Plan exceeds the Potential Marketing Budget and must be revised");
    }
    if (record.hasExistingLockbox === null) baseBlocks.push("Lockbox Yes / No response");
    if (record.hasExistingLockbox === true && !String(record.existingLockboxSerial || "").trim()) baseBlocks.push("Lockbox Serial Number");
    if (record.openHouseRequested && record.decisions?.openHouse === "Returned") openHouseLeadership.push("Open House changes requested; independent resubmission is required");
    if (!record.targetLiveDate) advisory.push("Target live date");
  } else {
    if (record.kind === "seller" && !record.linkedListing) baseBlocks.push("Existing ROOTS Listing / Pre-List selection");
    if (!(Number(record.salePrice) > 0)) baseBlocks.push("Contract / sale price");
    if (!record.acceptanceDate) baseBlocks.push("Mutual acceptance date");
    if (!record.closingDate) baseBlocks.push("Estimated closing date");
    if (!record.financing) baseBlocks.push("Financing type or Cash");
    if (record.financing !== "Cash" && !record.lender) operations.push("Lender contact information");
    if (!record.titleParty) operations.push("Title / settlement / attorney contact information");
  }

  const requiredDoc = record?.kind === "listing" ? "Executed Listing Agreement" : "Purchase and Sale Agreement";
  if (!record?.docs?.some(item => item.type === requiredDoc)) baseBlocks.push(requiredDoc);
  const dup = duplicateCheck(record || {});
  if (dup.warning && !duplicateResolutionCurrent(record)) baseLeadership.push("Potential duplicate requires acknowledgment and resolution note for the current transaction identity");
  if (agentMismatch(record)) baseLeadership.push("Assigned Agent mismatch must be reviewed by Leadership");

  return {
    baseBlocks,
    marketingBlocks,
    baseLeadership,
    marketingLeadership,
    openHouseLeadership,
    operations,
    advisory,
    blocks: [...baseBlocks, ...marketingBlocks],
    leadership: [...baseLeadership, ...marketingLeadership, ...openHouseLeadership]
  };
}

export function canSubmit(record) {
  const dup = duplicateCheck(record);
  if (dup.blocked) return { ok: false, reason: dup.blockedMsg };
  const gates = submissionGates(record);
  if (record?.status === "Needs Changes") {
    return gates.baseBlocks.length ? { ok: false, reason: "Resolve the Base Transaction Blocks Submission items before resubmitting the base transaction." } : { ok: true };
  }
  return gates.blocks.length ? { ok: false, reason: "Resolve the Blocks Submission items before submitting." } : { ok: true };
}

export function isBaseLocked(record) {
  return Boolean(record && BASE_STATUS_LOCKED.has(record.status));
}

export function submitMarketingVersion(record, source = "Agent submission") {
  const next = clone(record);
  next.marketingVersionCounter = Number(next.marketingVersionCounter || 0) + 1;
  const id = `MKT-${String(next.marketingVersionCounter).padStart(3, "0")}`;
  next.marketingSubmittedVersionId = id;
  next.marketingSubmittedBudgetBasis = marketingBudgetBasisSignature(next);
  next.marketingDecisionVersionId = "";
  next.marketingSubmitted = true;
  next.marketingRevisionRequired = false;
  next.decisions.budget = "Pending";
  next.marketingVersions.push({ id, budgetBasis: next.marketingSubmittedBudgetBasis, decision: "Pending", source, stale: false });
  next.marketingExecutionLines = next.marketingExecutionLines.map(line => ({ ...line, historical: true }));
  for (const item of marketingLineItems(next)) {
    const historicalComplete = next.marketingExecutionLines.find(line => line.catalogItemId === item.id && line.executionStatus === "Completed" && Math.abs(Number(line.approvedFinalPrice) - Number(item.amount)) < 0.01);
    if (historicalComplete) continue;
    next.marketingExecutionLineCounter += 1;
    next.marketingExecutionLines.push({
      lineId: `ML-${String(next.marketingExecutionLineCounter).padStart(4, "0")}`,
      catalogItemId: item.id,
      label: item.label,
      marketingVersionId: id,
      approvedFinalPrice: item.amount,
      executionStatus: "Waiting for Marketing Budget Approval",
      historical: false
    });
  }
  return next;
}

export function submitOpenHouseVersion(record, source = "Agent submission") {
  const next = clone(record);
  next.openHouseVersionCounter = Number(next.openHouseVersionCounter || 0) + 1;
  const id = `OHV-${String(next.openHouseVersionCounter).padStart(3, "0")}`;
  next.openHouseSubmittedVersionId = id;
  next.openHouseDecisionVersionId = "";
  next.openHouseSubmitted = true;
  next.decisions.openHouse = "Pending";
  next.openHouseVersions.push({ id, requestId: next.openHouseRequestId, decision: "Pending", source });
  return next;
}

export function submitBase(record) {
  const validation = canSubmit(record);
  if (!validation.ok) return { record: clone(record), validation };
  let next = clone(record);
  const resubmit = next.status === "Needs Changes";
  next.status = "Awaiting Leadership Approval";
  next.saved = true;
  next.dirty = false;
  next.decisions.base = "Pending";
  if (next.kind === "listing" && !resubmit) {
    next = submitMarketingVersion(next, "Initial Listing submission");
    if (next.openHouseRequested) next = submitOpenHouseVersion(next, "Initial Listing submission");
  }
  return { record: next, validation };
}

export function markVersionDecision(record, stream, decision) {
  const next = clone(record);
  if (stream === "marketing") {
    const id = next.marketingSubmittedVersionId;
    const version = next.marketingVersions.find(item => item.id === id);
    if (!id || !version) return next;
    version.decision = decision;
    next.marketingDecisionVersionId = id;
    next.marketingSubmitted = false;
    next.decisions.budget = decision;
    return next;
  }
  const id = next.openHouseSubmittedVersionId;
  const version = next.openHouseVersions.find(item => item.id === id);
  if (!id || !version) return next;
  version.decision = decision;
  next.openHouseDecisionVersionId = id;
  next.openHouseSubmitted = false;
  next.decisions.openHouse = decision;
  return next;
}

export function listingLifecycleRank(status) {
  const ranks = { Draft: 0, "Needs Changes": 0, "Awaiting Leadership Approval": 1, Approved: 2, "Operations Intake": 3, "Pre-List": 4, Listing: 5, Pending: 5, Closed: 6, Sold: 6 };
  return ranks[String(status || "")] ?? 0;
}

export function releasePaidMarketingAfterApproval(record) {
  const next = clone(record);
  if (next.kind !== "listing" || next.decisions?.budget !== "Approved") return next;
  const rank = listingLifecycleRank(next.status);
  next.marketingExecutionLines = next.marketingExecutionLines.map(line => {
    if (line.historical || line.executionStatus === "Completed" || line.executionStatus !== "Waiting for Marketing Budget Approval") return line;
    if (line.catalogItemId === "photo-package" && rank >= 4) return { ...line, executionStatus: "Queued" };
    if (line.catalogItemId === "just-listed" && rank >= 5) return { ...line, executionStatus: "In Progress" };
    if (line.catalogItemId === "just-sold" && rank >= 6) return { ...line, executionStatus: "In Progress" };
    return { ...line, executionStatus: "Authorized for Fulfillment" };
  });
  return next;
}

export function holdPaidMarketingUntilApproval(record) {
  const next = clone(record);
  next.marketingExecutionLines = next.marketingExecutionLines.map(line => line.historical || line.executionStatus === "Completed" ? line : { ...line, executionStatus: "Waiting for Marketing Budget Approval" });
  return next;
}

export function approveMarketing(record) {
  let next = markVersionDecision(record, "marketing", "Approved");
  next.marketingRevisionRequired = false;
  next.allowMarketingEdit = false;
  return releasePaidMarketingAfterApproval(next);
}

export function returnMarketing(record) {
  let next = markVersionDecision(record, "marketing", "Returned");
  next.marketingRevisionRequired = false;
  next.allowMarketingEdit = true;
  return holdPaidMarketingUntilApproval(next);
}

export function approveOpenHouse(record) {
  const next = markVersionDecision(record, "openHouse", "Approved");
  next.openHouseOpsConfirmed = false;
  next.allowOpenHouseEdit = false;
  return next;
}

export function returnOpenHouse(record) {
  const next = markVersionDecision(record, "openHouse", "Returned");
  next.openHouseOpsConfirmed = false;
  next.allowOpenHouseEdit = true;
  return next;
}

export function ancillaryExitKeepEditAvailable(stream, record) {
  if (stream === "openHouse") return Boolean(record?.decisions?.openHouse === "Returned");
  if (stream === "marketing") return Boolean(record?.decisions?.budget === "Returned" || record?.marketingRevisionRequired);
  return false;
}

export function closeAncillaryCorrection(session, stream, { discardSaved = true, keepEditAvailable = false } = {}) {
  const next = clone(session);
  next.activeCorrection = null;
  if (stream === "openHouse") {
    next.openHouseDraft = null;
    next.openHouseEditSnapshot = null;
    next.openHouseDirty = false;
    if (discardSaved) next.openHouseSavedDraft = null;
    if (next.record) next.record.allowOpenHouseEdit = Boolean(keepEditAvailable);
  } else if (stream === "marketing") {
    next.marketingDraft = null;
    next.marketingEditSnapshot = null;
    next.marketingDirty = false;
    if (discardSaved) next.marketingSavedDraft = null;
    if (next.record) next.record.allowMarketingEdit = Boolean(keepEditAvailable);
  }
  return next;
}

export function exitAncillaryWithoutSaving(session, stream) {
  return closeAncillaryCorrection(session, stream, {
    discardSaved: false,
    keepEditAvailable: ancillaryExitKeepEditAvailable(stream, session.record)
  });
}

export function saveAncillaryDraftAndExit(session, stream, draft) {
  const next = clone(session);
  if (stream === "openHouse") next.openHouseSavedDraft = clone(draft);
  if (stream === "marketing") next.marketingSavedDraft = clone(draft);
  return closeAncillaryCorrection(next, stream, {
    discardSaved: false,
    keepEditAvailable: ancillaryExitKeepEditAvailable(stream, next.record)
  });
}

export function createSession(record) {
  return {
    record: clone(record),
    activeCorrection: null,
    openHouseDraft: null,
    openHouseSavedDraft: null,
    openHouseEditSnapshot: null,
    openHouseDirty: false,
    marketingDraft: null,
    marketingSavedDraft: null,
    marketingEditSnapshot: null,
    marketingDirty: false,
    baseSavedDraft: null,
    baseDirty: false
  };
}
